/**
 * Resolve the API base URL for the current page.
 *
 * In local development every frontend runs on its own hostname (shop.localhost:3001,
 * admin.localhost:5173, ...) and talks to the one local `web` process under that same hostname on
 * :8080. Cookies are scoped by host, not port, so this keeps the shop's cookies first-party while
 * isolating them from the other apps' cookies (notably admin's dev `jwt` cookie, which would
 * otherwise override the shopper's session on every API call). Plain http://localhost:3001 keeps
 * calling http://localhost:8080.
 *
 * The derivation only kicks in when both the page and the configured URL are plain-http
 * localhost / *.localhost origins, so deployed builds, `start-applepay` (https, :8081) and an
 * explicit REACT_APP_WEB_API_URL pointing at a remote API are returned untouched. This is gated on
 * the hostname rather than REACT_APP_DEV_MODE because some deployed builds set that flag too.
 */
const DEFAULT_LOCAL_API_PORT = '8080';

function isLocalHostname(hostname: string) {
  return hostname === 'localhost' || hostname.endsWith('.localhost');
}

export function resolveWebApiUrl(
  envUrl: string,
  location: Pick<Location, 'protocol' | 'hostname'> | undefined = typeof window === 'undefined'
    ? undefined
    : window.location,
): string {
  if (!location || location.protocol !== 'http:' || !isLocalHostname(location.hostname)) {
    return envUrl;
  }

  let configured: URL;
  try {
    configured = new URL(envUrl);
  } catch {
    return envUrl;
  }
  if (configured.protocol !== 'http:' || !isLocalHostname(configured.hostname)) {
    return envUrl;
  }

  return `http://${location.hostname}:${configured.port || DEFAULT_LOCAL_API_PORT}`;
}
